import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCookiePreferences } from "@/hooks/useCookiePreferences";
import { Settings, Shield, BarChart3, Heart, Cog } from "lucide-react";

export function CookieStatus() {
  const { t } = useTranslation();
  const { preferences, hasConsent } = useCookiePreferences();
  const [showDetails, setShowDetails] = useState(false);

  if (!hasConsent()) {
    return null;
  }

  const getCookieIcon = (category: keyof typeof preferences) => {
    switch (category) {
      case 'essential': return <Shield className="w-3 h-3" />;
      case 'analytics': return <BarChart3 className="w-3 h-3" />;
      case 'preferences': return <Heart className="w-3 h-3" />;
      case 'functionality': return <Cog className="w-3 h-3" />;
      default: return null;
    }
  };

  const enabledCount = Object.values(preferences).filter(Boolean).length;
  const totalCount = Object.keys(preferences).length;

  return (
    <div className="flex items-center space-x-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowDetails(!showDetails)}
        className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <Settings className="w-3 h-3 mr-1" />
        Cookies: {enabledCount}/{totalCount}
      </Button>
      
      {showDetails && (
        <div className="absolute bottom-full mb-2 right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-3 min-w-[200px]">
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-slate-900 dark:text-white">
              {t('gdpr.categories.title')}
            </h4>
            {Object.entries(preferences).map(([category, enabled]) => (
              <div key={category} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {getCookieIcon(category as keyof typeof preferences)}
                  <span className="text-xs text-slate-600 dark:text-slate-300">
                    {t(`gdpr.categories.${category}.title`)}
                  </span>
                </div>
                <Badge 
                  variant={enabled ? "default" : "secondary"} 
                  className="text-xs px-1 py-0"
                >
                  {enabled ? t('gdpr.enabled') : t('gdpr.disabled')}
                </Badge>
              </div>
            ))}
            <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
              <Button
                variant="link"
                size="sm"
                onClick={() => {
                  // Clear preferences to show banner again
                  localStorage.removeItem('gdpr-cookies-accepted');
                  window.location.reload();
                }}
                className="text-xs p-0 h-auto text-primary"
              >
                {t('gdpr.customize')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
